import { useEffect } from 'react'
import type { FC } from 'react'
import { useThesaurusSearch } from '../../hooks/useThesaurusSearch'
import { ThesaurusIndexView } from './ThesaurusIndexView'
import { ThesaurusSynonymView } from './ThesaurusSynonymView'
import type { ThesaurusEntry } from '../../types/wordTools'

interface ThesaurusSearchTabProps {
  /** Current search term to query */
  searchTerm: string
  /** Called when search is cleared */
  onClear: () => void
  /** Called when user wants to collect word and switch to rhymes tab */
  onCollectAndExploreRhymes?: (word: string) => void
}

/**
 * Thesaurus search tab content for exploration worksheet.
 *
 * Features:
 * - Loading skeleton (300ms minimum) for consistent UX
 * - Cached result indicator during background refresh
 * - Friendly empty/error states with retry guidance
 * - Sense browsing (index view) and synonym exploration
 * - Back navigation from synonyms to index
 */
export const ThesaurusSearchTab: FC<ThesaurusSearchTabProps> = ({
  searchTerm,
  onClear,
  onCollectAndExploreRhymes
}) => {
  const {
    indexResults,
    synonymResults,
    isLoading,
    error,
    isCached,
    isStale,
    currentView,
    searchIndex,
    searchSynonyms,
    goBack,
    clearResults
  } = useThesaurusSearch()

  // Trigger search when term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      searchIndex(searchTerm)
    } else {
      clearResults()
    }
  }, [searchTerm, searchIndex, clearResults])

  // Handle sense card click - navigate to synonyms view
  const handleSenseClick = (entry: ThesaurusEntry) => {
    searchSynonyms([entry])
  }

  // Loading skeleton - shows for at least 300ms
  if (isLoading && !isCached) {
    return (
      <div data-testid="thesaurus-search-loading" className="space-y-3 p-4 animate-pulse">
        <div className="h-20 bg-gray-200 rounded-lg"></div>
        <div className="h-20 bg-gray-200 rounded-lg"></div>
        <div className="h-20 bg-gray-200 rounded-lg"></div>
        <div className="h-20 bg-gray-200 rounded-lg"></div>
      </div>
    )
  }

  // Error state with retry button
  if (error && !indexResults && !synonymResults) {
    return (
      <div data-testid="thesaurus-search-error" className="space-y-4 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl">⚠️</span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-1">
                Failed to load thesaurus
              </h3>
              <p className="text-sm text-red-700 mb-3">
                {error}
              </p>
              <button
                data-testid="thesaurus-search-retry"
                onClick={() => searchIndex(searchTerm)}
                className="text-sm font-medium text-red-700 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
          <p className="font-medium mb-1">Troubleshooting:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-600">
            <li>Check your internet connection</li>
            <li>Ensure backend API keys are configured (check <code>.env</code>)</li>
            <li>Try a different word</li>
            <li>Reload the page if the issue persists</li>
          </ul>
        </div>
      </div>
    )
  }

  // Empty state - no search term entered
  if (!searchTerm.trim()) {
    return (
      <div data-testid="thesaurus-search-empty" className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">📖</div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          Enter a word to explore senses
        </h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto">
          Type a word in the search box above to browse thesaurus senses and synonyms.
        </p>
      </div>
    )
  }

  // No results state - searched but no senses found
  if (indexResults && indexResults.entries.length === 0 && currentView === 'index') {
    return (
      <div data-testid="thesaurus-search-no-results" className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">🤔</div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No senses found for &ldquo;{indexResults.word}&rdquo;
        </h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto mb-4">
          Try a different word or check the spelling.
        </p>
        <button
          onClick={onClear}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
        >
          Clear search
        </button>
      </div>
    )
  }

  // Synonyms view - full interactive UI
  if (currentView === 'synonyms' && synonymResults) {
    return (
      <ThesaurusSynonymView
        synonymResults={synonymResults}
        sourceWord={indexResults?.word || searchTerm}
        onGoBack={goBack}
        onCollectAndExploreRhymes={onCollectAndExploreRhymes}
      />
    )
  }

  // Index view with sense cards
  if (indexResults) {
    return (
      <div data-testid="thesaurus-search-results" className="space-y-4">
        {/* Cache/Stale indicator */}
        {(isCached || isStale) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 mx-4 mt-4">
            {isStale ? (
              <span>📡 Updating results...</span>
            ) : (
              <span>💾 Showing cached results</span>
            )}
          </div>
        )}

        {/* Word info */}
        <div className="bg-gray-50 rounded-lg p-3 mx-4 space-y-1">
          <h3 className="text-lg font-semibold text-gray-900">{indexResults.word}</h3>
          <p className="text-xs text-gray-600">
            {indexResults.entries.length} sense{indexResults.entries.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Sense cards list */}
        <ThesaurusIndexView
          entries={indexResults.entries}
          word={indexResults.word}
          onSenseClick={handleSenseClick}
        />

        {/* Performance metadata */}
        <div className="text-xs text-gray-400 pt-4 border-t mx-4">
          <p>Processing time: {indexResults.processing_time_ms}ms</p>
        </div>
      </div>
    )
  }

  return null
}
